import { useEffect, useMemo, useState } from "react";
import { adminBookings, adminListHolidays, adminListIntensiveOffers } from "../lib/api";
import { ADMIN_SLOT_TIMES } from "../lib/site";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plane, Hourglass, Check } from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONDAY_CLOSED_FROM = "2026-09-07";

function isoDate(d) {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; }
function startOfGridForMonth(d) {
  const first = startOfMonth(d);
  const wd = (first.getDay() + 6) % 7;
  const grid = new Date(first); grid.setDate(first.getDate() - wd);
  return grid;
}
function transmissionForDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const wd = d.getDay();
  if (wd === 0) return null;
  if (wd === 1 && dateStr >= MONDAY_CLOSED_FROM) return null;
  if (wd === 5) return "auto";
  return "manual";
}

/**
 * Interactive month-grid calendar for the Intensive Offers "New offer" form.
 * Mirrors the master admin calendar so instructors can see all existing
 * bookings / holidays / held offers while clicking empty slots to pick them
 * into the current offer.
 *
 * Props:
 *   selected: Array<{date, time}>  — slots currently added to the offer
 *   onChange: (next: Array)        — called when the user toggles a slot
 *   transmission: "manual" | "auto" — current form choice (visual hint only)
 */
export default function IntensiveSlotPicker({ selected, onChange, transmission }) {
  const [bookings, setBookings] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [offers, setOffers] = useState([]);
  const [month, setMonth] = useState(() => startOfMonth(new Date()));

  useEffect(() => {
    Promise.all([
      adminBookings().catch(() => []),
      adminListHolidays().catch(() => []),
      adminListIntensiveOffers().catch(() => []),
    ]).then(([b, h, o]) => {
      setBookings(Array.isArray(b) ? b : []);
      setHolidays(Array.isArray(h) ? h : []);
      setOffers(Array.isArray(o) ? o : []);
    });
  }, []);

  const selectedSet = useMemo(
    () => new Set((selected || []).map(s => `${s.date}|${s.time}`)),
    [selected]
  );

  const cellMap = useMemo(() => {
    const m = {};
    bookings.forEach(b => {
      if (b.status === "cancelled") return;
      (b.slots || []).forEach(s => {
        const k = `${s.date}|${s.time}`;
        if (!m[k]) m[k] = [];
        m[k].push(b);
      });
    });
    return m;
  }, [bookings]);

  const holidayMap = useMemo(() => {
    const m = {};
    holidays.forEach(h => {
      if (!m[h.date]) m[h.date] = { byTime: {}, reasons: new Set() };
      m[h.date].byTime[h.time] = { reason: h.reason || "" };
      if (h.reason) m[h.date].reasons.add(h.reason);
    });
    Object.keys(m).forEach(d => {
      const times = Object.keys(m[d].byTime);
      m[d].allBlocked = ADMIN_SLOT_TIMES.every(t => times.includes(t));
    });
    return m;
  }, [holidays]);

  const heldMap = useMemo(() => {
    const m = {};
    const now = Date.now();
    offers.forEach(o => {
      if (o.status !== "pending") return;
      const exp = o.expires_at ? Date.parse(o.expires_at) : 0;
      if (!exp || exp < now) return;
      (o.slots || []).forEach(s => {
        const k = `${s.date}|${s.time}`;
        if (!m[k]) m[k] = [];
        m[k].push(o);
      });
    });
    return m;
  }, [offers]);

  const days = useMemo(() => {
    const start = startOfGridForMonth(month);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i);
      return d;
    });
  }, [month]);

  const monthLabel = month.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const goMonth = (delta) => setMonth(prev => {
    const x = new Date(prev); x.setMonth(prev.getMonth() + delta); return startOfMonth(x);
  });

  const toggleSlot = (date, time) => {
    const key = `${date}|${time}`;
    const current = selected || [];
    if (selectedSet.has(key)) {
      onChange(current.filter(s => !(s.date === date && s.time === time)));
    } else {
      onChange([...current, { date, time }].sort(
        (a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time)
      ));
    }
  };

  return (
    <div data-testid="intensive-slot-picker">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-xs text-[#4B5563]">
          Click empty slots to add them to the offer. Grey = existing booking · Amber = holiday · Dashed = held by another offer.
        </div>
        <div className="flex items-center gap-1">
          <Button type="button" size="sm" variant="outline" className="border-black/15" onClick={() => goMonth(-1)} data-testid="picker-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button type="button" size="sm" variant="outline" className="border-black/15 min-w-[150px]" onClick={() => setMonth(startOfMonth(new Date()))}>
            <CalendarIcon className="h-4 w-4 mr-1.5" /> {monthLabel}
          </Button>
          <Button type="button" size="sm" variant="outline" className="border-black/15" onClick={() => goMonth(1)} data-testid="picker-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="bg-white border border-black/5 rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 bg-[#F9FAFB] border-b border-black/5">
          {WEEKDAYS.map((d, i) => (
            <div key={d} className={`px-2 py-1.5 text-[9px] uppercase tracking-[0.2em] font-semibold ${i >= 5 ? "text-[#9CA3AF]" : "text-[#4B5563]"}`}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, idx) => {
            const ds = isoDate(d);
            const inMonth = d.getMonth() === month.getMonth();
            const today = isoDate(new Date());
            const isToday = ds === today;
            const dayTx = transmissionForDate(ds);
            const isClosed = !dayTx;
            const hInfo = holidayMap[ds];
            const allBlocked = !!hInfo?.allBlocked;
            const txMismatch = dayTx && transmission && dayTx !== transmission;
            return (
              <div
                key={idx}
                className={`relative border-t border-l border-black/5 min-h-[128px] p-1 ${allBlocked ? "bg-amber-50/60" : isClosed ? "bg-[#F9FAFB]" : "bg-white"} ${!inMonth ? "opacity-40" : ""} ${(idx + 1) % 7 === 0 ? "border-r-0" : ""}`}
                data-testid={`picker-day-${ds}`}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <span className={`text-xs font-medium ${isToday ? "h-5 w-5 inline-grid place-items-center rounded-full bg-[#111827] text-white" : "text-[#111827]"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                    {d.getDate()}
                  </span>
                  {dayTx && inMonth && !allBlocked && (
                    <span className={`text-[9px] uppercase tracking-[0.1em] font-medium ${dayTx === "auto" ? "text-[#37B160]" : "text-[#4B5563]"} ${txMismatch ? "opacity-50" : ""}`}>
                      {dayTx}
                    </span>
                  )}
                </div>

                {allBlocked ? (
                  <div className="rounded-md border border-amber-300 bg-amber-100/70 px-1.5 py-1" title={[...(hInfo?.reasons || [])].join(" · ")}>
                    <div className="flex items-center gap-1 text-amber-800">
                      <Plane className="h-2.5 w-2.5 flex-none" />
                      <span className="text-[9px] font-semibold uppercase tracking-[0.1em]">Holiday</span>
                    </div>
                  </div>
                ) : !dayTx ? (
                  <div className="text-[9px] text-[#9CA3AF] italic px-1">Closed to new bookings</div>
                ) : (
                  <div className="space-y-0.5">
                    {ADMIN_SLOT_TIMES.map(t => {
                      const key = `${ds}|${t}`;
                      const booked = cellMap[key] && cellMap[key][0];
                      const slotBlocked = hInfo?.byTime?.[t];
                      const heldHere = heldMap[key];
                      const isSelected = selectedSet.has(key);
                      const canPick = inMonth && !!dayTx && !booked && !slotBlocked && !heldHere;
                      return (
                        <button
                          type="button"
                          key={t}
                          onClick={() => canPick && toggleSlot(ds, t)}
                          disabled={!canPick}
                          className={[
                            "w-full text-left rounded-md px-1.5 py-1 transition-colors border",
                            isSelected
                              ? "bg-[#37B160] text-white border-[#37B160] shadow-sm"
                              : booked
                                ? "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB] cursor-not-allowed"
                                : slotBlocked
                                  ? "bg-amber-100/70 text-amber-800 border-amber-300 cursor-not-allowed"
                                  : heldHere
                                    ? "bg-amber-50/70 text-amber-800 border-dashed border-amber-400 cursor-not-allowed"
                                    : canPick
                                      ? "bg-white text-[#111827] border-black/10 hover:border-[#37B160] hover:bg-[#37B160]/5"
                                      : "bg-[#F9FAFB] text-[#9CA3AF] border-transparent cursor-not-allowed",
                          ].join(" ")}
                          data-testid={`picker-slot-${ds}-${t.replace(":", "")}`}
                          title={
                            booked ? `Booked: ${booked.customer_name}` :
                            slotBlocked ? (slotBlocked.reason ? `Holiday: ${slotBlocked.reason}` : "Holiday") :
                            heldHere ? `Held for ${heldHere[0]?.customer_name || "intensive offer"}` :
                            isSelected ? "Click to remove from offer" :
                            canPick ? "Click to add to offer" : ""
                          }
                        >
                          <div className={`text-[9px] uppercase tracking-[0.1em] ${isSelected ? "text-white/80" : "text-[#9CA3AF]"}`}>{t}</div>
                          {isSelected ? (
                            <div className="flex items-center gap-1 text-[10px] font-semibold">
                              <Check className="h-2.5 w-2.5" /> Added
                            </div>
                          ) : booked ? (
                            <div className="text-[10px] font-medium truncate">{booked.customer_name}</div>
                          ) : slotBlocked ? (
                            <div className="flex items-center gap-1">
                              <Plane className="h-2.5 w-2.5 flex-none" />
                              <span className="text-[9px] font-semibold uppercase tracking-[0.05em]">Holiday</span>
                            </div>
                          ) : heldHere ? (
                            <div className="flex items-center gap-1">
                              <Hourglass className="h-2.5 w-2.5 flex-none" />
                              <span className="text-[9px] font-semibold uppercase tracking-[0.05em]">Held</span>
                            </div>
                          ) : (
                            <div className="text-[10px] text-[#9CA3AF]">Available</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
